import { useState, useEffect } from "react"
import Dialog from "@mui/material/Dialog"
import {
  DialogTitle,
  Drawer,
  List,
  ListItemText,
  ListItemIcon,
  DialogActions,
  Button,
  Divider,
  DialogContent,
  TextField,
  ListItemButton,
} from "@mui/material"
import {
  DeleteForeverRounded,
  EditRounded,
  LibraryAddRounded,
} from "@mui/icons-material"
import { useUpdateTag } from "./helpers"
import { useTag } from "./helpers"
import { useRemoveTag } from "./useRemoveTag"
import { useNavigate, generatePath } from "react-router"
import { ROUTES } from "../navigation/routes"
import { Checkbox } from "../common/Checkbox"
import { CancelButton } from "../common/forms/CancelButton"

export const TagActionsDrawer = ({
  openWith,
  onClose,
}: {
  openWith: string | undefined
  onClose: () => void
}) => {
  const { data: tag } = useTag(openWith)
  const editTag = useUpdateTag()
  const [isEditTagDialogOpenedWithId, setIsEditTagDialogOpenedWithId] =
    useState<string | undefined>(undefined)
  const { mutate: removeTag } = useRemoveTag()
  const navigate = useNavigate()

  return (
    <>
      <Drawer
        anchor="bottom"
        open={!!openWith}
        onClose={onClose}
        transitionDuration={0}
      >
        <List>
          <ListItemButton
            onClick={() => setIsEditTagDialogOpenedWithId(openWith)}
          >
            <ListItemIcon>
              <EditRounded />
            </ListItemIcon>
            <ListItemText primary="Rename" />
          </ListItemButton>
          <ListItemButton
            onClick={() =>
              openWith &&
              editTag({ _id: openWith, isProtected: !tag?.isProtected })
            }
          >
            <ListItemIcon>
              <Checkbox selected={!!tag?.isProtected} />
            </ListItemIcon>
            <ListItemText
              primary="Mark as protected"
              secondary="This will lock and hide books behind it. Use unlock features to display them"
            />
          </ListItemButton>
          <ListItemButton
            onClick={() =>
              openWith &&
              editTag({ _id: openWith, isBlurEnabled: !tag?.isBlurEnabled })
            }
          >
            <ListItemIcon>
              <Checkbox selected={!!tag?.isBlurEnabled} />
            </ListItemIcon>
            <ListItemText
              primary="Blur covers"
              secondary="Apply a blur filter on book covers. Useful for sensitive content"
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              onClose()
              if (openWith) {
                navigate(generatePath(ROUTES.TAG_BOOKS, { id: openWith }))
              }
            }}
          >
            <ListItemIcon>
              <LibraryAddRounded />
            </ListItemIcon>
            <ListItemText primary="Manage books" />
          </ListItemButton>
        </List>
        <Divider />
        <List>
          <ListItemButton
            onClick={() => {
              openWith && removeTag({ _id: openWith })
              onClose()
            }}
          >
            <ListItemIcon>
              <DeleteForeverRounded />
            </ListItemIcon>
            <ListItemText primary="Remove" />
          </ListItemButton>
        </List>
      </Drawer>
      <EditTagDialog
        id={isEditTagDialogOpenedWithId}
        onClose={() => {
          setIsEditTagDialogOpenedWithId(undefined)
          onClose()
        }}
        open={!!isEditTagDialogOpenedWithId}
      />
    </>
  )
}

const EditTagDialog = ({
  onClose,
  open,
  id,
}: {
  open: boolean
  id: string | undefined
  onClose: () => void
}) => {
  const [name, setName] = useState("")
  const { data: tag } = useTag(id)
  const { name: tagName } = tag ?? {}
  const editTag = useUpdateTag()

  const onInnerClose = () => {
    setName("")
    onClose()
  }

  const onConfirm = (id: string, name: string) => {
    if (name) {
      editTag({ _id: id, name })
    }
  }

  useEffect(() => {
    void id

    setName((prev) => tagName || prev)
  }, [tagName, id])

  return (
    <Dialog onClose={onInnerClose} open={open}>
      <DialogTitle>Tag: {tagName}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onInnerClose} variant="outlined" />
        <Button
          onClick={() => {
            onInnerClose()
            id && onConfirm(id, name)
          }}
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
